import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SDLogo } from '@/components/atoms';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileState } from '@/contexts/ProfileContext';
import { auth as authApi } from '@/lib/api';

export default function ResetPassword() {
  const { user, refresh } = useAuth();
  const { savedProfile, analysis, reload } = useProfileState();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sessionEmail, setSessionEmail] = useState(user?.email || null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Verify the user actually has a valid session before showing the form.
  // We call /api/auth/me directly so we don't race the AuthContext state update.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.me();
        if (cancelled) return;
        if (res.user) {
          setSessionEmail(res.user.email);
          setCheckingSession(false);
        } else {
          navigate('/', { replace: true });
        }
      } catch {
        if (!cancelled) navigate('/', { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      await authApi.setPassword(password);
      setSuccess(true);
      await refresh();
      await reload();
      // Give the user a moment to see the confirmation, then route them forward.
      setTimeout(() => {
        if (analysis) navigate('/dashboard');
        else if (savedProfile?.pivot_type) navigate('/onboarding/review');
        else if (savedProfile?.name) navigate('/onboarding/confirm');
        else navigate('/onboarding/connect');
      }, 900);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save the new password. Please try again.';
      setError(msg);
      setBusy(false);
    }
  };

  // If the user got here without an active session, send them back to Welcome.
  // Wait for auth to finish loading before deciding — handled via the /me check above.

  return (
    <div className="min-h-screen bg-sd-ivory flex flex-col" data-testid="reset-password-page">
      <header className="px-8 py-5"><SDLogo /></header>
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-[440px] w-full sd-card p-8 animate-fade-in">
          <h1 className="text-[24px] font-semibold text-sd-black mb-1">Set a new password</h1>
          <p className="text-sd-mid-gray text-[14px] mb-6">
            {checkingSession
              ? 'Verifying your session…'
              : (<>You are signed in as <span className="font-medium text-sd-black">{sessionEmail || 'your account'}</span>. Choose a new password below.</>)
            }
          </p>
          <form onSubmit={submit} className="space-y-3">
            <input
              type="password"
              placeholder="New password (min 8 chars)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="sd-input"
              autoFocus
              required
              disabled={checkingSession}
              data-testid="reset-new-password"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="sd-input"
              required
              disabled={checkingSession}
              data-testid="reset-confirm-password"
            />
            {error && <div className="text-[13px] text-sd-error" data-testid="reset-error">{error}</div>}
            {success && <div className="text-[13px] text-sd-primary font-medium" data-testid="reset-success">Password updated. Taking you to your dashboard…</div>}
            <button
              className="sd-btn-primary w-full"
              type="submit"
              disabled={busy || success || checkingSession}
              data-testid="reset-submit"
            >
              {busy ? 'Saving…' : success ? '✓ Saved' : 'Save new password →'}
            </button>
          </form>
          <div className="text-center mt-5 text-[12.5px] text-sd-mid-gray">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="hover:text-sd-black underline underline-offset-2"
              data-testid="reset-cancel"
            >
              Cancel and go back
            </button>
          </div>
        </div>
      </main>
      <footer className="px-8 py-5 text-center text-[12px] text-sd-mid-gray">
        SD·Pivot · Built for sustainability professionals who want a real career move.
      </footer>
    </div>
  );
}
